import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Banner, Button, Card, EmptyState, Mono, PageBody, PageHeader, StackedList } from "../ui/Field";

type Rev = { revision: number; locale: string; author_id: string | null; created_at: number };

export default function PageRevisions() {
  const { slug, page } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState<Rev[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.get<{ items: Rev[] }>(`/api/sites/${slug}/pages/${page}/revisions`)
    .then((r) => setItems(r.items))
    .catch((e) => setErr((e as Error).message));
  useEffect(() => { if (slug && page) load(); /* eslint-disable-next-line */ }, [slug, page]);

  async function restore(rev: number) {
    if (!confirm(`Restore revision ${rev}? This creates a new draft from that snapshot.`)) return;
    try {
      await api.post(`/api/sites/${slug}/pages/${page}/revisions/${rev}/restore`);
      nav(`/sites/${slug}/pages/${page}`);
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <PageBody>
      <Link to={`/sites/${slug}/pages/${page}`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] no-underline">← Editor</Link>
      <PageHeader
        eyebrow="№ Page · History"
        title={<>Revision <em>history</em></>}
        description="Every save creates a revision. Restore appends — your current draft is never lost."
      />
      {err && <Banner kind="error">{err}</Banner>}
      <Card bodyClassName="p-0">
        {items.length === 0 ? (
          <div className="p-6"><EmptyState title="No revisions yet." /></div>
        ) : (
          <StackedList className="px-5">
            {items.map((r) => (
              <li key={r.revision + r.locale} className="flex items-center justify-between gap-4 py-4">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="display text-2xl text-[var(--color-ink)]">#{r.revision}</span>
                    <Mono className="text-[11px]">{r.locale}</Mono>
                  </div>
                  <div className="text-[11.5px] text-[var(--color-muted)] mt-0.5 font-mono">{new Date(r.created_at).toLocaleString()}</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => restore(r.revision)}>Restore as new draft</Button>
              </li>
            ))}
          </StackedList>
        )}
      </Card>
    </PageBody>
  );
}
